/**
 * Таблица обработок системы — центр макета
 * `docs/superpowers/prototypes/season-planning-2027/07-system-editor.html`.
 *
 * Строка читается как фраза агронома: «против чего → чем → когда». Порядок
 * колонок макета: # · фенофаза и окно · против чего · тех.операция · препарат
 * с нормой · сцепка · ₽/га.
 *
 * Колонка «Против чего» показывается ТОЛЬКО у системы защиты (правые боксы
 * макета): у питания её место занимает приём и вынос, у обработки почвы —
 * глубина, и пустой столбец там читался бы как «забыли заполнить».
 */
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatMaterial,
  formatPerHa,
  formatWindowShort,
  formatWindow,
  type CardCost,
  type TechCardMaterial,
} from '@/lib/planning/techCards';
import {
  showsTargetColumn,
  type ModuleKind,
  type ModuleOperation,
} from '@/lib/planning/techCardModules';

export interface SystemOperationActions {
  onEdit: (op: ModuleOperation) => void;
  onEditMaterials: (op: ModuleOperation) => void;
  onDelete: (op: ModuleOperation) => void;
}

export function SystemOperationsTable({
  kind,
  operations,
  materials,
  cost,
  canWrite,
  actions,
}: {
  kind: ModuleKind;
  operations: ModuleOperation[];
  materials: Record<string, TechCardMaterial[]>;
  cost: CardCost | null;
  canWrite: boolean;
  actions: SystemOperationActions;
}) {
  const withTarget = showsTargetColumn(kind);
  const perOp = new Map((cost?.operations ?? []).map((o) => [o.operation_id, o]));

  if (operations.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        В системе ещё нет ни одной обработки. Строка — это «против чего → чем → когда»:
        добавьте первую, и появится стоимость на гектар.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">#</TableHead>
            <TableHead>Фенофаза и окно</TableHead>
            {withTarget && <TableHead>Против чего</TableHead>}
            <TableHead>Тех.операция</TableHead>
            <TableHead>Препарат и норма на га</TableHead>
            <TableHead>Сцепка</TableHead>
            <TableHead className="text-right">₽/га</TableHead>
            {canWrite && <TableHead className="text-right">Действия</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {operations.map((op, i) => {
            const rows = materials[op.id] ?? op.materials ?? [];
            const money = perOp.get(op.id);
            return (
              <TableRow key={op.id}>
                <TableCell className="tabular-nums text-muted-foreground">{i + 1}</TableCell>
                <TableCell>
                  <div className="font-medium text-foreground">
                    {op.phase_label ?? op.name ?? 'фенофаза не указана'}
                  </div>
                  <div className="text-xs text-muted-foreground" title={formatWindow(op)}>
                    {formatWindowShort(op)}
                  </div>
                </TableCell>
                {withTarget && (
                  <TableCell>
                    {op.target_label ? (
                      <Badge variant="primary">{op.target_label}</Badge>
                    ) : op.notes ? (
                      <span className="text-muted-foreground">{op.notes}</span>
                    ) : (
                      <span className="text-muted-foreground">не указано</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-foreground">
                  {op.op_type_name ?? 'работа без названия'}
                </TableCell>
                <TableCell>
                  {rows.length === 0 ? (
                    <Badge variant="accent-brand">препарат не выбран</Badge>
                  ) : (
                    <div className="space-y-1">
                      {rows.map((m) => (
                        <div key={m.id} className="text-sm text-foreground">
                          {formatMaterial(m)}
                        </div>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {op.machinery_unit_name ?? '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatPerHa(money?.total ?? null)}
                </TableCell>
                {canWrite && (
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => actions.onEdit(op)}>
                        Правка
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => actions.onEditMaterials(op)}
                      >
                        Препараты
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => actions.onDelete(op)}>
                        Убрать
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
